import type { CrewlinkClient } from '../supabase-client';
import type { CaptureRow, CaptureStatus } from '../database.types';

/**
 * Writer wrapper for `create_capture` (migrations 0004 and 0005).
 * M3 / S-03-01, hardened in S-03-01A.
 *
 * The layering rule (ARCHITECTURE §2, INV-T02): UI → query hooks → writer
 * wrappers → Postgres RPC. UI code never calls `.rpc(...)` directly and never
 * calls `.insert(...)` at all.
 *
 * WHAT CROSSES THIS BOUNDARY, AND WHAT DELIBERATELY DOES NOT:
 *
 *   The Project, the idempotency key, the transcript, the creation timezone and
 *   optionally the status. **Not the owner.** Ownership is read from
 *   `auth.uid()` inside the function, so there is no argument through which a
 *   caller could claim another owner — spoofing is not something the API can
 *   express rather than something it refuses (INV-T23).
 *
 *   Not the Capture number either. `CAP-####` is allocated server-side from the
 *   Project's counter inside the same transaction (D-016).
 *
 * ONE CALL CREATES THREE ROWS — the Capture, transcript version 1 and the
 * creation Work Log event — atomically (INV-T06). There is no partial success
 * to handle here, which is why this wrapper has no compensating logic.
 */

export interface CreateCaptureInput {
  readonly projectId: string;
  /**
   * Generated on the DEVICE before the first attempt, and REUSED on every
   * retry of that same save. That is the whole mechanism: the same key returns
   * the same Capture instead of creating a second one (D-002, INV-T06).
   *
   * A fresh key per attempt would defeat it silently — two taps would become
   * two Captures, and nothing would look broken.
   */
  readonly clientCaptureUuid: string;
  /**
   * The Final Accepted Transcript, or null.
   *
   * Blank and whitespace-only are treated as absent by the server: no
   * transcript-version row is created, and the Capture is still valid (PT §5).
   */
  readonly transcript: string | null;
  /** IANA zone in force at creation. Provenance only (D-009, D-016). */
  readonly timezone: string;
  /** Defaults to OPEN server-side. */
  readonly status?: CaptureStatus;
}

export async function createCapture(
  client: CrewlinkClient,
  input: CreateCaptureInput,
): Promise<CaptureRow> {
  const { data, error } = await client.rpc('create_capture', {
    p_project_id: input.projectId,
    p_client_capture_uuid: input.clientCaptureUuid,
    p_transcript: input.transcript,
    p_timezone: input.timezone,
    ...(input.status ? { p_status: input.status } : {}),
  });

  if (error) throw error;
  if (!data) throw new Error('create_capture returned no capture row');

  return data;
}

/**
 * Turn a failure into something Brandon can act on.
 *
 * The database is the authority — these translate its conditions rather than
 * re-implementing the rules in the client, where the two could drift apart.
 *
 * **`CL001` is Crewlink's own code for "the Project is CLOSED".** It is
 * deliberately not `55000`, which already means a stale-status write elsewhere
 * and carries a completely different message; matching on the wrong one would
 * put the wrong sentence in front of Brandon (D-015).
 *
 * **`P0002` covers "no such Project" AND "not your Project" as ONE case, and
 * the wording must keep them one case.** Distinguishing them in the message
 * would leak across users exactly what the server refuses to leak in the error
 * code (INV-T23).
 *
 * **`CL002` and `CL003` are the timezone conditions (S-03-01A, finding F6).**
 * They exist so this function never has to match on `capture_timezone_shape` —
 * a database CONSTRAINT NAME that a future migration is free to rename, and
 * whose raw message is Postgres noise rather than a sentence for Brandon. They
 * are two codes rather than one because they are two different mistakes: a
 * device that reported no zone, and a device that reported a wrong one.
 */
export function describeCreateCaptureError(message: string, code?: string): string {
  const normalised = `${code ?? ''} ${message}`.toLowerCase();

  if (normalised.includes('cl001') || normalised.includes('project is closed')) {
    return 'This Project is closed, so new Captures cannot be added to it.';
  }
  if (normalised.includes('p0002') || normalised.includes('project not found')) {
    return 'That Project is not available. Go back and open it again.';
  }
  if (normalised.includes('not authenticated') || normalised.includes('42501')) {
    return 'Your session has expired. Sign in again and try once more.';
  }
  if (normalised.includes('cl002') || normalised.includes('timezone is required')) {
    return 'Could not read this device’s time zone, so the Capture was not saved.';
  }
  if (normalised.includes('cl003') || normalised.includes('not a valid iana')) {
    return 'This device reported a time zone Crewlink does not recognise.';
  }
  // Kept as a LAST-RESORT net only. The two codes above are the contract; this
  // catches a constraint violation arriving from some path that bypassed the
  // writer's own checks, and it must never become the primary match.
  if (normalised.includes('capture_timezone_shape')) {
    return 'Could not read this device’s time zone, so the Capture was not saved.';
  }
  if (normalised.includes('failed to fetch') || normalised.includes('network')) {
    return 'Could not reach Crewlink. Check your connection and try again.';
  }
  return 'Could not save the Capture. Please try again.';
}
